import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { StartupProfile, InvestorProfile } from '../../types/firestore';
import StartupProfileComponent from '../profile/StartupProfile';
import InvestorProfileComponent from '../profile/InvestorProfile';
import ImageCropModal from './ImageCropModal';
import { formatDate } from '../../utils/date';
import { pitchDeckFrameSrc } from '../../utils/pitchDeckFrameSrc';

interface UserProfileViewProps {
  userId: string;
  userType: 'startup' | 'investor';
  onClose: () => void;
  /** Hides the top-right Close button (admin user list → investor). A back link is shown instead. */
  hideHeaderClose?: boolean;
  /** Removes the sticky "Startup/Investor Profile" bar (admin deal pipeline). */
  hideProfileHeader?: boolean;
  /** Admin deals popup: hides action buttons and shows pitch deck as a link instead of inline embed. */
  adminCompact?: boolean;
}

const UserProfileView: React.FC<UserProfileViewProps> = ({
  userId,
  userType,
  onClose,
  hideHeaderClose = false,
  hideProfileHeader = false,
  adminCompact = false,
}) => {
  const dismissActionLabel = hideHeaderClose ? 'Back' : 'Close';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<StartupProfile | InvestorProfile | null>(null);

  // Approval states
  const [approving, setApproving] = useState(false);
  const [savingFeatured, setSavingFeatured] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const getProfileFunction = httpsCallable(
          functions,
          userType === 'startup' ? 'getStartupProfile' : 'getInvestorProfile'
        );
        const result = await getProfileFunction({ userId });
        const data = result.data as any;

        if (data?.success && data.profile) {
          setProfile(data.profile);
          // Sync featured status
          if (userType === 'startup' && data.profile.featured !== undefined) {
            setIsFeatured(data.profile.featured);
          }
        } else {
          setError('Profile not found');
        }
      } catch (err: any) {
        console.error('Error loading profile:', err);
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (userId && userType) {
      loadProfile();
    }
  }, [userId, userType]);

  // Create object URL for thumbnail preview
  useEffect(() => {
    if (thumbnailFile) {
      const objectUrl = URL.createObjectURL(thumbnailFile);
      setThumbnailPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setThumbnailPreview(null);
    }
  }, [thumbnailFile]);

  const handleApproveUser = async () => {
    if (!userId || !userType) return;

    // For startups, thumbnail is required if not already present
    if (userType === 'startup' && !thumbnailFile && !(profile as StartupProfile)?.thumbnailUrl) {
      setError('A thumbnail is required to approve a startup.');
      return;
    }

    try {
      setApproving(true);
      setError(null);

      if (userType === 'investor') {
        const updateInvestorProfile = httpsCallable(functions, 'updateInvestorProfile');
        const result = await updateInvestorProfile({
          userId,
          updates: {
            approved: true,
            approvedAt: new Date(),
          },
        });

        const data = result.data as any;
        if (data.success && data.profile) {
          setProfile(data.profile);
        } else {
          throw new Error(data.error || 'Failed to approve investor');
        }
      } else if (userType === 'startup') {
        // Upload thumbnail if provided
        if (thumbnailFile) {
          const reader = new FileReader();
          const base64Data = await new Promise<string>((resolve, reject) => {
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]);
            };
            reader.onerror = reject;
            reader.readAsDataURL(thumbnailFile);
          });

          const uploadStartupThumbnail = httpsCallable(functions, 'uploadStartupThumbnail');
          const uploadResult = await uploadStartupThumbnail({
            userId,
            file: {
              name: thumbnailFile.name,
              contentType: thumbnailFile.type,
              data: base64Data,
            },
          });

          const uploadData = uploadResult.data as any;
          if (!uploadData.success) {
            throw new Error(uploadData.error || 'Failed to upload thumbnail');
          }
        }

        // Then update the approval status
        const updateStartupProfile = httpsCallable(functions, 'updateStartupProfile');
        const result = await updateStartupProfile({
          userId,
          updates: {
            approved: true,
            approvedAt: new Date(),
            featured: isFeatured === true,
          },
        });

        const data = result.data as any;
        if (data.success && data.profile) {
          setProfile(data.profile);
        } else {
          throw new Error(data.error || 'Failed to approve startup');
        }
      }
    } catch (err: any) {
      console.error('Error approving user:', err);
      setError(err.message || 'Failed to approve user');
    } finally {
      setApproving(false);
    }
  };

  const handleFeaturedChange = async (next: boolean) => {
    const previous = isFeatured;
    setIsFeatured(next);
    if (profile?.approved !== true) return;
    try {
      setSavingFeatured(true);
      setError(null);
      const updateStartupProfile = httpsCallable(functions, 'updateStartupProfile');
      const result = await updateStartupProfile({
        userId,
        updates: { featured: next },
      });
      const data = result.data as { success?: boolean; profile?: StartupProfile; error?: string };
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to update featured status');
      }
      if (data.profile) setProfile(data.profile);
    } catch (err: unknown) {
      console.error('Error updating featured status:', err);
      setIsFeatured(previous);
      setError(err instanceof Error ? err.message : 'Failed to update featured status');
    } finally {
      setSavingFeatured(false);
    }
  };

  const heightClass = hideProfileHeader ? '' : 'min-h-screen';

  if (loading) {
    return (
      <div className={`${heightClass} bg-dark-950 flex items-center justify-center py-16`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className={`${heightClass} bg-dark-950 flex items-center justify-center py-16`}>
        <div className="text-center px-4">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            {dismissActionLabel}
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`${heightClass} bg-dark-950 flex items-center justify-center py-16`}>
        <div className="text-center px-4">
          <p className="text-white text-lg mb-4">No profile data available</p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            {dismissActionLabel}
          </button>
        </div>
      </div>
    );
  }

  const isApproved = profile.approved === true;
  const approvedAt = profile.approvedAt;
  const formattedApprovedDate = approvedAt ? formatDate(approvedAt) : null;

  const startupProfile = userType === 'startup' ? (profile as StartupProfile) : null;
  const adminPendingReview = userType === 'startup' && !isApproved && !adminCompact;
  const adminEffectiveThumbnail =
    thumbnailPreview?.trim() || startupProfile?.thumbnailUrl?.trim() || '';
  const adminStartupRightPanel =
    startupProfile && !adminCompact ? (
      <div className="hidden lg:flex lg:flex-col lg:w-[65%] sticky top-[3.5rem] h-[calc(100vh-3.5rem)] min-h-0 border-l-8 border-black">
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-visible-dark flex flex-col gap-4 p-2">
          {startupProfile.pitchDeckUrl ? (
            <div className="w-full aspect-[16/9] overflow-hidden border border-gray-800">
              <iframe
                src={pitchDeckFrameSrc(startupProfile.pitchDeckUrl)}
                className="w-full h-full border-0 block"
                title="Pitch Deck"
              />
            </div>
          ) : adminEffectiveThumbnail ? (
            <div className="w-full aspect-[16/9] overflow-hidden border border-gray-800 relative">
              <img
                src={adminEffectiveThumbnail}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <p className="text-gray-300 text-sm">No pitch deck uploaded yet</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 border border-gray-800 rounded-lg">
              <p className="text-gray-500 text-sm">No pitch deck uploaded yet</p>
            </div>
          )}
        </div>
      </div>
    ) : undefined;

  return (
    <div className={`${heightClass} bg-[#262626]`}>
      {hideProfileHeader ? null : (
        <div className="sticky top-0 z-50 bg-dark-900/90 backdrop-blur-md border-b border-gray-800 px-6 py-4 shadow-xl">
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {hideHeaderClose ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm font-medium text-gray-400 hover:text-white transition-colors w-fit"
                  >
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-white">
                    {userType === 'startup' ? 'Startup' : 'Investor'} Profile
                  </h2>
                </div>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                    title="Return to list"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-emerald-500">Review</span>{' '}
                    {userType === 'startup' ? 'Startup' : 'Investor'}
                  </h2>
                </>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {error && <span className="text-red-400 text-sm font-medium">{error}</span>}
              {isApproved ? (
                <div className="flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/50 rounded-full text-emerald-400 text-sm font-medium">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Approved {formattedApprovedDate && `on ${formattedApprovedDate}`}
                </div>
              ) : (
                <div className="flex items-center px-4 py-2 bg-amber-500/10 border border-amber-500/50 rounded-full text-amber-400 text-sm font-medium animate-pulse">
                  Pending Approval
                </div>
              )}
              {userType === 'startup' && (
                <label
                  className={`flex items-center space-x-3 ${
                    savingFeatured ? 'cursor-wait opacity-70' : 'cursor-pointer'
                  }`}
                >
                  <div className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={isFeatured}
                      disabled={savingFeatured}
                      onChange={e => void handleFeaturedChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </div>
                  <span className="text-white text-sm font-medium">Featured Startup</span>
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail + approve stay below until the startup is approved. */}
      {!isApproved && !adminCompact && userType === 'startup' && (
        <div className="bg-gray-800/50 border-b border-gray-700 p-4">
          <div className="container mx-auto flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center space-x-6 flex-wrap gap-y-4">
              <div className="flex items-center space-x-3">
                <label className="cursor-pointer group flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg border border-gray-600 transition-all">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setImagePreview(reader.result as string);
                          setShowCropModal(true);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                  />
                  <svg
                    className="w-5 h-5 text-gray-400 group-hover:text-emerald-400 transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-white text-sm font-medium">
                    {thumbnailFile || (profile as StartupProfile).thumbnailUrl
                      ? 'Change Thumbnail'
                      : 'Set Thumbnail'}
                  </span>
                </label>

                {(thumbnailPreview || (profile as StartupProfile).thumbnailUrl) && (
                  <div className="relative group">
                    <img
                      src={thumbnailPreview || (profile as StartupProfile).thumbnailUrl}
                      alt="Thumbnail preview"
                      className="w-20 h-12 object-cover rounded border border-gray-600 shadow-lg group-hover:border-emerald-500 transition-colors"
                    />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-gray-800 shadow-md"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {error && <span className="text-red-400 text-sm font-medium">{error}</span>}
              {!isApproved && (
                <button
                  onClick={handleApproveUser}
                  disabled={
                    approving ||
                    (userType === 'startup' &&
                      !thumbnailFile &&
                      !(profile as StartupProfile).thumbnailUrl)
                  }
                  className="relative overflow-hidden group bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold px-8 py-3 rounded-xl shadow-lg shadow-emerald-900/20 transition-all active:scale-95"
                >
                  <div className="flex items-center space-x-2">
                    {approving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Approve Profile</span>
                      </>
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className={adminCompact ? '' : 'pb-20'}>
          {userType === 'startup' ? (
            <StartupProfileComponent
              profile={profile as StartupProfile}
              hideActions={true}
              compactDeck={true}
              pitchDeckOnDemand={adminCompact}
              rightPanel={adminStartupRightPanel}
              leftPanelTall={!adminCompact}
              previewThumbnailUrl={thumbnailPreview ?? undefined}
              adminPendingReview={adminPendingReview}
            />
          ) : (
            <InvestorProfileComponent
              profile={profile as InvestorProfile}
              adminCompact={adminCompact}
            />
          )}
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropModal && imagePreview && (
        <ImageCropModal
          imageSrc={imagePreview}
          onClose={() => {
            setShowCropModal(false);
            setImagePreview(null);
          }}
          onCropComplete={croppedFile => {
            setThumbnailFile(croppedFile);
            setShowCropModal(false);
            setImagePreview(null);
          }}
          aspectRatio={16 / 9}
        />
      )}
    </div>
  );
};

export default UserProfileView;
